import { Box } from '@chakra-ui/react'
import { ReactNode, Ref, useRef } from 'react'

import {
  BOUNDARY_ATTRIBUTE,
  useClickOutsideBoundary,
} from '../../hooks/useClickOutsideBoundary'

export interface ClickOutsideBoundaryProps {
  /**
   * Unique identifier for this boundary.
   * Used to match portaled elements with the same boundary ID.
   */
  boundaryId: string
  /**
   * Handler called when a click is detected outside the boundary
   */
  onClickOutside?: () => void
  /**
   * Whether click-outside detection is enabled.
   * @default true
   */
  enabled?: boolean
  /**
   * Children to render inside the boundary
   */
  children: ReactNode
  /**
   * Whether to automatically detect and exclude floating UI elements
   * @default true
   */
  excludeFloatingUI?: boolean
  ref?: Ref<HTMLDivElement>
}

/**
 * A wrapper component that detects clicks outside its boundary.
 *
 * This component is designed to work well with portaled elements like dropdown menus.
 * It uses a data-attribute system to mark elements that should be considered "inside"
 * the boundary, even if they're rendered elsewhere in the DOM via portals.
 *
 * ## Basic Usage
 *
 * ```tsx
 * <ClickOutsideBoundary
 *   boundaryId="my-popover"
 *   onClickOutside={() => setIsOpen(false)}
 *   enabled={isOpen}
 * >
 *   <PopoverContent>
 *     <SelectInput /> // Dropdown menus work correctly!
 *   </PopoverContent>
 * </ClickOutsideBoundary>
 * ```
 *
 * ## With Portaled Elements
 *
 * If you have custom portaled content that should be considered "inside" the boundary,
 * mark it with the `data-click-boundary` attribute:
 *
 * ```tsx
 * <Portal>
 *   <div data-click-boundary="my-popover">
 *     Custom portaled content
 *   </div>
 * </Portal>
 * ```
 */
export const ClickOutsideBoundary = ({
  boundaryId,
  onClickOutside,
  enabled = true,
  children,
  excludeFloatingUI = true,
  ref: forwardedRef,
  ...boxProps
}: ClickOutsideBoundaryProps) => {
  const internalRef = useRef<HTMLDivElement>(null)
  const ref = (forwardedRef as React.RefObject<HTMLDivElement>) ?? internalRef

  useClickOutsideBoundary({
    ref,
    boundaryId,
    onClickOutside: onClickOutside ?? (() => {}),
    enabled: enabled && Boolean(onClickOutside),
    excludeFloatingUI,
  })

  return (
    <Box ref={ref} {...{ [BOUNDARY_ATTRIBUTE]: boundaryId }} {...boxProps}>
      {children}
    </Box>
  )
}

ClickOutsideBoundary.displayName = 'ClickOutsideBoundary'
